// Dashboard renderer. `single` = the primary widget full-bleed; `grid` = a
// non-scrolling gridCols × gridRows canvas (this route is in __root's
// height-bounded wideMainPatterns, so the canvas owns the viewport remainder
// and never scrolls). Widgets are agent-authored HTML in sandboxed iframes —
// see WidgetFrame for the bridge/security model. The dashboard is polled and
// each frame re-renders only when its own updatedAt moves.
import { useClient } from '@cortex/react-query';
import type {
  Dashboard,
  DashboardCallableTool,
  DashboardShare,
  DashboardWidgetMeta,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { DashboardLayout } from '../../components/dashboards/DashboardLayout';
import { WidgetFrame } from '../../components/dashboards/WidgetFrame';
import { recordDashboardVisit } from '../../lib/dashboard-recency';

export const Route = createFileRoute('/dashboards/$dashboardId')({ component: DashboardPage });

function sharesKey(dashboardId: string) {
  return ['dashboards', dashboardId, 'shares'] as const;
}

/** Dates arrive as ISO strings over HTTP even though the type says `Date`. */
function fmtDate(v: Date | string | null | undefined): string {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ShareRow({ dashboardId, share }: { dashboardId: string; share: DashboardShare }) {
  const client = useClient();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const revoked = Boolean(share.revokedAt);
  const url = `${window.location.origin}/share/${share.id}`;

  const revoke = useMutation({
    mutationFn: () => client.http.delete(`v1/dashboards/${dashboardId}/shares/${share.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: sharesKey(dashboardId) }),
  });

  return (
    <div style={{ padding: 'var(--sp-3) 0', borderTop: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'var(--fw-med)' }}>{share.label || 'Unlabeled share'}</span>
        {revoked ? (
          <span className="badge badge--neutral">Revoked</span>
        ) : (
          <span className="badge badge--tool">Active</span>
        )}
        <span className="badge badge--neutral">
          {share.viewCount} view{share.viewCount === 1 ? '' : 's'}
        </span>
      </div>
      <p className="t-xs t-muted" style={{ marginTop: 2 }}>
        Created {fmtDate(share.createdAt)} · Last viewed {fmtDate(share.lastViewedAt)}
      </p>
      {!revoked ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginTop: 4 }}>
          <input
            className="input t-mono"
            readOnly={true}
            value={url}
            style={{ flex: 1, minWidth: 0 }}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => {
              navigator.clipboard?.writeText(url).then(
                () => setCopied(true),
                () => setCopied(false),
              );
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            disabled={revoke.isPending}
            onClick={() => {
              if (window.confirm('Revoke this share link? It will stop resolving immediately.')) {
                revoke.mutate();
              }
            }}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke'}
          </button>
        </div>
      ) : null}
      {revoke.error ? (
        <p className="t-xs" style={{ color: 'var(--status-danger)' }}>
          {(revoke.error as Error).message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Public share links for this dashboard — anyone with the URL can view it
 * (and trigger the same widget tool calls) without logging in. Lives behind
 * a button + modal, same shell as AestheticsButton on the All dashboards page.
 */
function ManageSharesButton({ dashboardId }: { dashboardId: string }) {
  const client = useClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');

  const shares = useQuery({
    queryKey: sharesKey(dashboardId),
    queryFn: () => client.http.get(`v1/dashboards/${dashboardId}/shares`).json<DashboardShare[]>(),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      client.http.post(`v1/dashboards/${dashboardId}/shares`, {
        json: label.trim() ? { label: label.trim() } : {},
      }),
    onSuccess: () => {
      setLabel('');
      return qc.invalidateQueries({ queryKey: sharesKey(dashboardId) });
    },
  });

  return (
    <>
      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setOpen(true)}>
        Manage Shares
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Public share links">
        <p className="t-xs t-muted" style={{ marginTop: 0 }}>
          Anyone with a share link can view this dashboard — and trigger any MCP tool its widgets
          call — without logging in. Revoke a link to cut off access immediately.
        </p>
        <form
          style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <label className="field" style={{ flex: 1 }}>
            <span className="field-label">Label (optional)</span>
            <input
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Lobby wallboard"
            />
          </label>
          <button type="submit" className="btn btn--primary btn--sm" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create share'}
          </button>
        </form>
        {create.error ? (
          <p className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(create.error as Error).message}
          </p>
        ) : null}

        {shares.isLoading ? (
          <p className="t-sm t-secondary">Loading…</p>
        ) : shares.error ? (
          <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
            Failed to load: {(shares.error as Error).message}
          </p>
        ) : !shares.data || shares.data.length === 0 ? (
          <p className="t-sm t-secondary" style={{ marginTop: 'var(--sp-3)' }}>
            No share links yet.
          </p>
        ) : (
          <div style={{ marginTop: 'var(--sp-2)' }}>
            {shares.data.map((share) => (
              <ShareRow key={share.id} dashboardId={dashboardId} share={share} />
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}

function DashboardPage() {
  const { dashboardId } = Route.useParams();
  const client = useClient();
  // Feed the nav rail's "Recent" folder (localStorage; see dashboard-recency).
  useEffect(() => recordDashboardVisit(dashboardId), [dashboardId]);

  const dashboard = useQuery({
    queryKey: ['dashboards', dashboardId],
    queryFn: () =>
      client.http.get(`v1/dashboards/${dashboardId}`, { cache: 'no-store' }).json<Dashboard>(),
    refetchInterval: 10_000,
  });
  // Org-wide annotated callable set — the second gate of every bridge call.
  // Same 10s cadence so revoking an annotation severs widgets promptly.
  const callable = useQuery({
    queryKey: ['dashboards', 'callable-tools'],
    queryFn: () =>
      client.http
        .get('v1/dashboards/callable-tools', { cache: 'no-store' })
        .json<DashboardCallableTool[]>(),
    refetchInterval: 10_000,
  });

  if (dashboard.isLoading) return <Spinner />;
  if (dashboard.error) {
    return (
      <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)' }}>
        Failed to load: {(dashboard.error as Error).message}
      </div>
    );
  }
  const d = dashboard.data;
  if (!d) return null;
  const callableTools = callable.data ?? [];

  return (
    <>
      <PageHeader title={d.name} actions={<ManageSharesButton dashboardId={d.id} />} />
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <DashboardLayout
          kind={d.kind}
          gridCols={d.gridCols}
          gridRows={d.gridRows}
          primaryWidgetId={d.primaryWidgetId}
          widgets={d.widgets}
          renderWidget={(w: DashboardWidgetMeta) => (
            <WidgetFrame dashboardId={d.id} widget={w} callableTools={callableTools} />
          )}
        />
      </div>
    </>
  );
}
